import { localized, msg, str } from '@lit/localize';
import { Task as LitTask } from '@lit/task';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import type {
  Annotation,
  Connection,
  Id,
  LoopIterationContext,
  PendingInteraction,
  Task,
  TaskApprovalGuards,
  TaskDefinition,
  TaskGroup,
  TaskInteractionResponse,
  TaskStateDetail,
  User,
  Visitor,
  WorkflowAuditSummary,
  WorkflowSummary,
  WorkflowVersionEvent,
} from '../../api/types';
import './components/workflows';
import './components/tasks-tree';
import './components/inspector';
import './components/settings';
import './components/workflow-agent/workflow-agent';
import './components/task-details-bench';
import './components/template-chooser';
import type { TaskDetailsBenchViewModel } from './components/task-details-bench';
import './components/workflow-user-input-dialog';
import './components/overview';
import './components/version-history-panel';
import '../../components/confirmation-dialog';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/tabs/sp-tabs.js';
import '@spectrum-web-components/tabs/sp-tabs-overflow.js';
import '@spectrum-web-components/tabs/sp-tab.js';
import '@spectrum-web-components/split-view/sp-split-view.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/underlay/sp-underlay.js';
import '@spectrum-web-components/button/sp-button.js';
import { api } from '../../api';
import { type TaskData, taskService } from '../../services/task-service';
import { visitorService } from '../../services/visitor-service';
import {
  WebsocketService,
  websocketService,
} from '../../services/websocket-service';
import {
  type WorkflowData,
  workflowService,
} from '../../services/workflow-service';
import { formatDateTime } from '../../utils/format';
import { createEditor } from './components/rete';
import type { WorkflowUserInputRequest } from './dialogs/types';
import type { EditorApi } from './editor-api';
import {
  createBlankWorkflow,
  importWorkflowFile,
  openWorkflowStartChooser,
} from './start-workflow-actions';
import {
  syncLocalTaskConfigUpdate,
  syncRemoteTaskConfigUpdate,
} from './task-config-sync';
import { cursorHeartbeatMs, isSingleVisitor } from './visitor-utils';
import { handleWorkflowEvent } from './workflow-event-handler';

function toWorkflowUserInputRequest(
  summary: PendingInteraction | null | undefined,
  definitionMap: Map<string, TaskDefinition>
): WorkflowUserInputRequest | undefined {
  if (
    !summary ||
    typeof summary.taskId !== 'number' ||
    typeof summary.taskType !== 'string'
  ) {
    return undefined;
  }

  const definition = definitionMap.get(summary.taskType);
  const interaction = summary.interaction ?? definition?.interaction;
  if (!interaction) return undefined;

  return {
    taskId: summary.taskId,
    taskType: summary.taskType,
    input: summary.input ?? {},
    config: summary.config ?? {},
    interaction,
  };
}

const TASK_DETAILS_BENCH_EXPANDED_KEY = 'kv.taskDetailsBench.expanded';
const TASK_DETAILS_BENCH_HEIGHT_KEY = 'kv.taskDetailsBench.height';
const TASK_DETAILS_BENCH_DEFAULT_HEIGHT = 280;
const TASK_DETAILS_BENCH_MIN_HEIGHT = 180;

@customElement('kv-module-workflow')
@localized()
export class KvModuleWorkflow extends LitElement {
  static styles = [
    css`
      :host {
        display: block;
        height: 100vh;
      }
      .builder {
        height: 100%;
      }
      .hidden {
        display: none;
      }
      sp-split-view.builder {
        width: 100%;
        height: 100%;
        --mod-splitview-handle-background-color: var(--color-app-border);
        --mod-splitview-handle-background-color-hover: var(--spectrum-gray-500);
      }
      .sidebar {
        box-sizing: border-box;
        background: var(--color-sidebar-surface);
        border-right: 1px solid var(--color-app-border);
        box-shadow: 8px 0 24px rgba(15, 23, 42, 0.03);
        overflow: hidden;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }
      .sidebar sb-workflows {
        margin: 14px 14px 10px;
      }
      .sidebar sp-tabs-overflow {
        margin-left: 14px;
        margin-right: 14px;
      }
      sp-tabs-overflow {
        flex: 0 0 auto;
        min-height: 0;
        max-width: calc(100% - 28px);
        --sp-tabs-overflow-next-button-right: 0;
        --sp-tabs-overflow-previous-button-left: 0;
      }
      sp-tabs {
        width: 100%;
      }
      .sidebar sp-tab {
        color: var(--spectrum-gray-600);
        font-size: 12px;
        font-weight: 500;
      }
      .sidebar sp-tab:hover {
        color: var(--spectrum-gray-900);
      }
      .sidebar sp-tab[selected] {
        color: var(--color-sidebar-selected-border);
        font-weight: 600;
      }
      .sidebar-tab-panel {
        flex: 1 1 auto;
        min-height: 0;
        overflow: auto;
        padding: 10px 14px 14px;
        scrollbar-width: thin;
      }
      .main {
        height: 100%;
        min-width: 0;
      }
      .editor-wrap {
        background: var(--panel);
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow: hidden;
        position: relative;
      }
      .canvas-wrap {
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
        position: relative;
      }
      .editor-underlay {
        position: absolute;
        inset: 0;
        z-index: 2;
      }
      .editor-spinner {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 3;
      }
      .rete {
        position: absolute;
        inset: 0;
        background-color: var(--color-editor-bg);
        background-image:
          radial-gradient(
            circle,
            var(--color-workbench-grid-dot-strong) 1.2px,
            transparent 1.3px
          ),
          radial-gradient(
            circle,
            var(--color-workbench-grid-dot) 1px,
            transparent 1.1px
          );
        background-size: 64px 64px, 16px 16px;
        background-position: 0 0, 8px 8px;
        z-index: 0;
      }
      .visitors {
        position: absolute;
        top: 8px;
        left: 8px;
        display: flex;
        gap: 8px;
        z-index: 1;
      }
      .visitor {
        display: flex;
        align-items: center;
        gap: 4px;
        background: var(--panel);
        padding: 2px 4px;
        border-radius: 4px;
        font-size: 12px;
      }
      .visitor .avatar {
        width: 16px;
        height: 16px;
        border-radius: 50%;
      }
    `,
  ];

  @property({ attribute: false }) accessor user!: User;

  @state() accessor activeView: 'overview' | 'builder' = 'overview';
  @state() accessor overviewWorkflows: WorkflowSummary[] = [];
  @state() accessor overviewLoading = false;
  @state() accessor overviewError: string | null = null;
  @state() accessor overviewRunIds = new Set<number>();
  @state() accessor overviewDeleteIds = new Set<number>();
  @state() accessor overviewLatestAudits = new Map<
    number,
    WorkflowAuditSummary
  >();

  @state() accessor selectedWorkflowId: Id | undefined = undefined;
  @state() accessor workflow: WorkflowSummary | undefined = undefined;
  @state() accessor annotations: Annotation[] = [];
  @state() accessor templates: WorkflowSummary[] = [];
  @state() accessor templatesLoading = false;
  @state() accessor templatesError: string | null = null;
  @state() accessor showTemplateChooser = false;
  @state() accessor templateApplying = false;
  @state() accessor loading = false;
  @state() accessor error: string | undefined = undefined;
  @state() accessor visitors: Visitor[] = [];
  @state() accessor activeTab = 'task-tree';
  @state() accessor selectedTaskId: Id | undefined = undefined;
  @state() accessor selectedTaskIds: Id[] = [];
  @state() accessor selectedTaskConfig: Record<string, unknown> | undefined =
    undefined;
  @state() accessor selectedTaskApprovalGuards: TaskApprovalGuards | undefined =
    undefined;
  @state() accessor selectedTaskDefinition: TaskDefinition | undefined =
    undefined;
  @state() accessor selectedTaskLabel: string | null = null;
  @state() accessor selectedConnectionId: Id | undefined = undefined;
  @state() accessor selectedConnection: Connection | undefined = undefined;
  @state() accessor selectedGroupIds: Id[] = [];
  @state() accessor taskGroups: TaskGroup[] = [];
  @state() accessor selectedGroupId: Id | undefined = undefined;
  @state() accessor groupEditTitle = '';
  @state() accessor previousTab = 'task-tree';
  @state() accessor userInputRequests: Map<number, WorkflowUserInputRequest> =
    new Map();
  @state() accessor userInputActionTaskId: number | undefined = undefined;
  @state() accessor userInputActionType: string | undefined = undefined;
  @state() accessor taskDetails: TaskDetailsBenchViewModel | undefined =
    undefined;
  @state() accessor taskDetailsBenchExpanded = false;
  @state() accessor taskDetailsBenchHeight = TASK_DETAILS_BENCH_DEFAULT_HEIGHT;
  @state() accessor versionsLoading = false;
  @state() accessor versionsLoadingMore = false;
  @state() accessor restoringVersionId: Id | null = null;
  @state() accessor pendingRestoreVersion: WorkflowVersionEvent | null = null;
  @state() accessor versionEvents: WorkflowVersionEvent[] = [];
  @state() accessor versionEventsNextCursor: Id | null = null;
  @state() accessor versionEventsHasMore = false;
  @state() accessor overviewUserInputRequests = new Map<
    number,
    WorkflowUserInputRequest
  >();

  private taskDataTask = new LitTask(this, {
    task: async () => taskService.fetchTaskData(),
    args: () => [],
  });

  constructor() {
    super();
    this.addController(this.taskDataTask);
  }

  private editorApi?: EditorApi;
  private editorHost?: HTMLElement;
  private overviewSockets = new Map<Id, WebsocketService>();
  private overviewPendingInteractionRevisions = new Map<number, number>();
  private pendingInteractionRevision = 0;
  private annotationSaveTimer?: number;
  private taskStateCache = new Map<number, TaskStateDetail>();
  private taskStateRequests = new Map<number, Promise<TaskStateDetail>>();
  private taskStateRequestVersions = new Map<number, number>();
  private taskStateRefreshQueuedIds = new Set<number>();
  private taskStateLoadingIds = new Set<number>();
  private workflowSocketReady: Promise<void> = Promise.resolve();
  @query('.rete') accessor editorContainer: HTMLElement | null = null;
  @query('sb-workflows') accessor workflowsList: any = null;
  @query('sb-task-tree')
  accessor taskTreeEl:
    | (HTMLElement & {
        focusSearch?: () => Promise<void> | void;
      })
    | null = null;
  @query('sp-tabs') accessor tabsEl: any = null;

  private pendingTaskSearchFocus = false;

  private getWorkflowIdFromUrl(): Id | undefined {
    const raw = new URLSearchParams(window.location.search).get('workflowId');
    if (!raw) return undefined;
    const id = Number(raw);
    return Number.isFinite(id) ? id : undefined;
  }

  private getWorkflowViewFromUrl(): 'overview' | 'builder' | undefined {
    const raw = new URLSearchParams(window.location.search).get('workflowView');
    if (raw === 'overview' || raw === 'builder') {
      return raw;
    }
    return undefined;
  }

  private getWorkflowTabFromUrl(): string | undefined {
    const raw = new URLSearchParams(window.location.search).get('workflowTab');
    const allowed = new Set([
      'task-tree',
      'workflow-agent',
      'inspector',
      'settings',
      'versions',
    ]);
    if (!raw) return undefined;
    return allowed.has(raw) ? raw : undefined;
  }

  private updateUrlState() {
    const url = new URL(window.location.href);
    if (this.selectedWorkflowId != null) {
      url.searchParams.set('workflowId', String(this.selectedWorkflowId));
    }
    url.searchParams.set('workflowView', this.activeView);
    url.searchParams.set('workflowTab', this.activeTab);
    window.history.replaceState({}, '', url);
  }

  private persistWorkflowSelection() {
    try {
      if (this.selectedWorkflowId != null) {
        localStorage.setItem(
          'kv.lastWorkflowId',
          String(this.selectedWorkflowId)
        );
      } else {
        localStorage.removeItem('kv.lastWorkflowId');
      }
    } catch (_) {
      /* no-op */
    }
    this.updateUrlState();
  }

  private persistActiveTab() {
    try {
      if (this.activeTab) {
        localStorage.setItem('kv.workflowTab', this.activeTab);
      } else {
        localStorage.removeItem('kv.workflowTab');
      }
    } catch (_) {
      /* no-op */
    }
    this.updateUrlState();
  }

  private clampTaskDetailsBenchHeight(height: number) {
    const maxHeight =
      typeof window === 'undefined'
        ? 560
        : Math.max(220, Math.floor(window.innerHeight * 0.58));
    return Math.max(
      TASK_DETAILS_BENCH_MIN_HEIGHT,
      Math.min(maxHeight, Math.round(height))
    );
  }

  private loadTaskDetailsBenchPreferences() {
    try {
      this.taskDetailsBenchExpanded =
        localStorage.getItem(TASK_DETAILS_BENCH_EXPANDED_KEY) === 'true';
      const storedHeight = Number(
        localStorage.getItem(TASK_DETAILS_BENCH_HEIGHT_KEY)
      );
      if (Number.isFinite(storedHeight)) {
        this.taskDetailsBenchHeight =
          this.clampTaskDetailsBenchHeight(storedHeight);
      }
    } catch (_) {
      /* no-op */
    }
  }

  private setTaskDetailsBenchExpanded(expanded: boolean) {
    this.taskDetailsBenchExpanded = expanded;
    try {
      localStorage.setItem(
        TASK_DETAILS_BENCH_EXPANDED_KEY,
        expanded ? 'true' : 'false'
      );
    } catch (_) {
      /* no-op */
    }
  }

  private setTaskDetailsBenchHeight(height: number) {
    const clamped = this.clampTaskDetailsBenchHeight(height);
    this.taskDetailsBenchHeight = clamped;
    try {
      localStorage.setItem(TASK_DETAILS_BENCH_HEIGHT_KEY, String(clamped));
    } catch (_) {
      /* no-op */
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this.onKeyDown, true);
    this.addEventListener('arrange-layout', this.arrangeLayout as any);
    this.addEventListener('workflow-modified', this.onWorkflowModified as any);
    this.loadTaskDetailsBenchPreferences();
    const tabFromUrl = this.getWorkflowTabFromUrl();
    if (tabFromUrl) {
      this.activeTab = tabFromUrl;
    } else {
      try {
        const storedTab = localStorage.getItem('kv.workflowTab');
        if (storedTab) this.activeTab = storedTab;
      } catch (_) {
        /* no-op */
      }
    }
    const workflowIdFromUrl = this.getWorkflowIdFromUrl();
    const viewFromUrl = this.getWorkflowViewFromUrl();
    if (viewFromUrl) {
      this.activeView = viewFromUrl;
    } else if (workflowIdFromUrl != null) {
      this.activeView = 'builder';
    }
    void this.loadOverviewWorkflows();
  }

  disconnectedCallback(): void {
    document.removeEventListener('keydown', this.onKeyDown, true);
    this.teardownEditor();
    this.disconnectOverviewSockets();
    this.removeEventListener('arrange-layout', this.arrangeLayout as any);
    this.removeEventListener(
      'workflow-modified',
      this.onWorkflowModified as any
    );
    super.disconnectedCallback();
  }

  protected async firstUpdated() {
    if (this.activeView === 'builder') {
      await this.ensureEditorReady();
      await this.loadInitialWorkflow();
    }
  }

  private async ensureEditorReady() {
    if (!this.editorContainer) return;
    if (this.editorApi) {
      if (this.editorHost === this.editorContainer) return;
      this.teardownEditor();
      if (!this.editorContainer) return;
    }

    const container = this.editorContainer;
    const editorApi = await createEditor(container);

    // The view may have changed while we awaited.
    if (!this.isConnected || this.editorContainer !== container) {
      editorApi?.destroy?.();
      return;
    }

    this.editorApi = editorApi;
    this.editorHost = container;
    const me = visitorService.init(this.user);
    this.editorApi?.setVisitorId?.(me.id);

    this.editorHost?.addEventListener(
      'task-double-clicked',
      this.onTaskDoubleClicked
    );
    window.addEventListener('task-double-clicked', this.onTaskDoubleClicked);
  }

  private teardownEditor() {
    if (this.editorHost) {
      this.editorHost.removeEventListener(
        'task-double-clicked',
        this.onTaskDoubleClicked
      );
      this.editorHost = undefined;
    }
    window.removeEventListener('task-double-clicked', this.onTaskDoubleClicked);
    websocketService.disconnect();
    this.workflowSocketReady = Promise.resolve();
    this.editorApi?.destroy?.();
    this.editorApi = undefined;
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('selectedWorkflowId')) {
      this.persistWorkflowSelection();
    }
    if (changed.has('activeTab')) {
      this.persistActiveTab();
    }
    if (changed.has('activeView')) {
      this.updateUrlState();
    }
    if (
      this.pendingTaskSearchFocus &&
      this.activeView === 'builder' &&
      this.activeTab === 'task-tree' &&
      this.taskTreeEl?.focusSearch
    ) {
      this.pendingTaskSearchFocus = false;
      void this.taskTreeEl.focusSearch();
    }
  }

  private async focusTaskSearch() {
    if (this.activeView !== 'builder') return;
    this.pendingTaskSearchFocus = true;
    if (this.activeTab !== 'task-tree') {
      this.activeTab = 'task-tree';
      await this.updateComplete;
    }
    if (this.taskTreeEl?.focusSearch) {
      this.pendingTaskSearchFocus = false;
      await this.taskTreeEl.focusSearch();
    }
  }

  private isTaskSearchHotkey(e: KeyboardEvent) {
    const key = e.key?.toLowerCase?.() ?? '';
    return (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && key === 'k';
  }
  private buildUserInputRequest(
    task: Task,
    definition?: TaskDefinition,
    inputOverride?: Record<string, unknown> | null
  ): WorkflowUserInputRequest | undefined {
    const taskId = Number(task.id);
    if (!Number.isFinite(taskId)) return undefined;
    if (task.state?.interactionPending !== true) return undefined;

    const interaction = definition?.interaction;
    if (!interaction) return undefined;

    const rawInput =
      inputOverride ??
      (task.state?.input as Record<string, unknown> | undefined) ??
      {};
    const inputData =
      (rawInput as { data?: Record<string, unknown> }).data ?? rawInput;
    const rawConfig =
      (task.config as Record<string, unknown> | undefined) ??
      ((task as any)?.config?.data as Record<string, unknown> | undefined) ??
      {};

    return {
      taskId,
      taskType: task.type,
      input: inputData,
      config: rawConfig,
      interaction,
    };
  }

  private async buildInitialUserInputRequests(
    workflowId: Id,
    tasks: Task[],
    definitionMap: Map<string, TaskDefinition>
  ): Promise<Map<number, WorkflowUserInputRequest>> {
    const entries = await Promise.all(
      tasks.map(async (task) => {
        const taskId = Number(task.id);
        let inputOverride: Record<string, unknown> | null | undefined;
        if (
          Number.isFinite(taskId) &&
          task.state?.interactionPending === true &&
          task.state.input === undefined
        ) {
          try {
            const state = await workflowService.getTaskState(
              workflowId,
              taskId
            );
            this.taskStateCache.set(taskId, state);
            this.applyTaskStateToEditor(taskId, state);
            inputOverride = state.input;
          } catch (error) {
            console.error('Failed to load pending interaction state', error);
          }
        }

        const request = this.buildUserInputRequest(
          task,
          definitionMap.get(task.type),
          inputOverride
        );
        return request ? ([request.taskId, request] as const) : undefined;
      })
    );

    return new Map(
      entries.filter(
        (entry): entry is readonly [number, WorkflowUserInputRequest] =>
          entry !== undefined
      )
    );
  }

  private async buildOverviewUserInputRequests(
    workflows: WorkflowSummary[]
  ): Promise<Map<number, WorkflowUserInputRequest>> {
    const withPending = workflows.filter(
      (workflow) => workflow.pendingInteraction != null
    );
    if (withPending.length === 0) {
      return new Map();
    }
    const definitions = await this.resolveTaskDefinitions();
    const definitionMap = new Map(definitions.map((d) => [d.name, d]));
    const next = new Map<number, WorkflowUserInputRequest>();

    for (const workflow of withPending) {
      if (typeof workflow.id !== 'number') continue;
      const request = toWorkflowUserInputRequest(
        workflow.pendingInteraction,
        definitionMap
      );
      if (request) {
        next.set(workflow.id, request);
      }
    }

    return next;
  }

  private setOverviewUserInputRequest(
    workflowId: number,
    request: WorkflowUserInputRequest | undefined
  ) {
    const next = new Map(this.overviewUserInputRequests);
    if (request) {
      next.set(workflowId, request);
    } else {
      next.delete(workflowId);
    }
    this.overviewUserInputRequests = next;
  }

  private setLatestOverviewAudit(
    workflowId: number,
    audit: WorkflowAuditSummary | undefined
  ) {
    const next = new Map(this.overviewLatestAudits);
    if (audit) {
      next.set(workflowId, audit);
    } else {
      next.delete(workflowId);
    }
    this.overviewLatestAudits = next;
  }

  private async loadOverviewFailedTaskErrors(
    workflowId: number,
    auditId: number
  ) {
    try {
      const detail = await api.workflowAudits.get(auditId);
      const failedTransitions = detail.transitions.filter((transition) =>
        transition.error?.trim()
      );
      const failedTransition = failedTransitions[0];
      const failedTaskErrors = failedTransition
        ? [
            {
              auditId,
              taskId: failedTransition.taskId,
              taskName: failedTransition.taskName || failedTransition.taskType,
              error: failedTransition.error ?? '',
            },
          ]
        : [];
      this.overviewWorkflows = this.overviewWorkflows.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              failedTaskErrors,
              failedTaskErrorCount: failedTransitions.length,
            }
          : workflow
      );
    } catch (error) {
      console.error('Failed to load overview task errors', error);
    }
  }

  private syncOverviewAuditSummaries(workflows: WorkflowSummary[]) {
    const next = new Map<number, WorkflowAuditSummary>();

    for (const workflow of workflows) {
      const workflowId = Number(workflow.id);
      if (!Number.isFinite(workflowId) || !workflow.lastExecutedAt) {
        continue;
      }

      next.set(workflowId, {
        id: Number(workflow.lastAuditId ?? workflowId),
        workflowId,
        workflowName: this.formatWorkflowName(workflow),
        trigger: 'manual',
        status: workflow.lastAuditStatus ?? 'running',
        startedAt: workflow.lastExecutedAt,
        finishedAt: null,
        durationMs: null,
      });
    }

    this.overviewLatestAudits = next;
  }

  private async applyOverviewPendingInteraction(
    workflowId: number,
    summary: PendingInteraction | null | undefined
  ) {
    const revision =
      (this.overviewPendingInteractionRevisions.get(workflowId) ?? 0) + 1;
    this.overviewPendingInteractionRevisions.set(workflowId, revision);

    const definitions = await this.resolveTaskDefinitions();
    if (this.overviewPendingInteractionRevisions.get(workflowId) !== revision) {
      return;
    }
    const definitionMap = new Map(definitions.map((d) => [d.name, d]));
    const request = toWorkflowUserInputRequest(summary, definitionMap);

    this.overviewWorkflows = this.overviewWorkflows.map((workflow) =>
      workflow.id === workflowId
        ? {
            ...workflow,
            pendingInteraction: summary ?? null,
          }
        : workflow
    );
    this.setOverviewUserInputRequest(workflowId, request);
  }

  private async refreshOverviewPendingInteraction(workflowId: number) {
    const revision =
      (this.overviewPendingInteractionRevisions.get(workflowId) ?? 0) + 1;
    this.overviewPendingInteractionRevisions.set(workflowId, revision);

    try {
      const workflow = await workflowService.get(workflowId);
      if (
        this.overviewPendingInteractionRevisions.get(workflowId) !== revision
      ) {
        return;
      }
      await this.applyOverviewPendingInteraction(
        workflowId,
        workflow.pendingInteraction ?? null
      );
    } catch (error) {
      console.error('Failed to refresh pending interaction', error);
    }
  }

  private async refreshWorkflowPendingInteraction(workflowId: number) {
    const revision = this.pendingInteractionRevision + 1;
    this.pendingInteractionRevision = revision;

    try {
      const [workflow, definitions] = await Promise.all([
        workflowService.get(workflowId),
        this.resolveTaskDefinitions(),
      ]);
      if (
        this.pendingInteractionRevision !== revision ||
        this.selectedWorkflowId !== workflowId
      ) {
        return;
      }

      const summary = workflow.pendingInteraction ?? null;
      const definitionMap = new Map(definitions.map((d) => [d.name, d]));
      const request = toWorkflowUserInputRequest(summary, definitionMap);
      this.workflow = this.workflow
        ? { ...this.workflow, pendingInteraction: summary }
        : workflow;
      this.userInputRequests = request
        ? new Map([[request.taskId, request]])
        : new Map();
      if (!request) {
        this.userInputActionTaskId = undefined;
        this.userInputActionType = undefined;
      }
    } catch (error) {
      console.error('Failed to refresh pending interaction', error);
    }
  }

  private async loadOverviewWorkflows() {
    this.overviewLoading = true;
    this.overviewError = null;
    try {
      this.overviewWorkflows = await workflowService.list();
      this.syncOverviewAuditSummaries(this.overviewWorkflows);
      this.overviewUserInputRequests =
        await this.buildOverviewUserInputRequests(this.overviewWorkflows);
      this.syncOverviewSockets();
    } catch (e) {
      this.overviewError = e instanceof Error ? e.message : String(e);
    } finally {
      this.overviewLoading = false;
    }
  }

  private syncOverviewSockets() {
    if (this.activeView !== 'overview') {
      this.disconnectOverviewSockets();
      return;
    }
    const workflowIds = new Set(
      this.overviewWorkflows
        .map((workflow) => workflow.id)
        .filter((id): id is number => typeof id === 'number')
    );

    for (const id of workflowIds) {
      if (this.overviewSockets.has(id)) continue;
      const socket = new WebsocketService();
      socket.connectToWorkflow(id, {
        onMessage: (data) => this.handleOverviewEvent(id, data),
        onError: (error) =>
          console.error('🚨 WebSocket error for overview feed', id, error),
      });
      this.overviewSockets.set(id, socket);
    }

    for (const [id, socket] of this.overviewSockets.entries()) {
      if (workflowIds.has(id)) continue;
      socket.disconnect();
      this.overviewSockets.delete(id);
    }
  }

  private disconnectOverviewSockets() {
    for (const socket of this.overviewSockets.values()) {
      socket.disconnect();
    }
    this.overviewSockets.clear();
  }

  private updateCursorThrottle() {
    const interval = cursorHeartbeatMs(this.visitors);
    this.editorApi?.setCursorUpdateInterval?.(interval);
  }

  private arrangeLayout = () => {
    this.editorApi?.layout();
  };

  private handleGroupHotkey(e: KeyboardEvent) {
    const key = e.key?.toLowerCase?.() ?? '';
    const code = e.code?.toLowerCase?.() ?? '';
    if (key === 'g' || code === 'keyg') {
      if (e.ctrlKey || e.metaKey) {
        if (e.shiftKey && this.selectedGroupId != null) {
          e.preventDefault();
          this.ungroup(this.selectedGroupId);
          return true;
        }
      }
      const selectionCount = new Set([
        ...this.selectedTaskIds,
        ...this.selectedGroupIds,
      ]).size;
      if (selectionCount > 1) {
        e.preventDefault();
        void this.groupSelectedTasks();
        return true;
      }
    }
    return false;
  }

  private openWorkflow = async (e: Event) => {
    const detail = (
      e as CustomEvent<{
        id: number;
        showTemplateChooser?: boolean;
      }>
    ).detail;
    if (!detail) return;
    this.showTemplateChooser = Boolean(detail.showTemplateChooser);
    if (this.showTemplateChooser) {
      void this.loadTemplates();
    }
    await this.openWorkflowInBuilder(detail.id);
  };

  private onWorkflowModified = async () => {
    if (this.activeView === 'builder' && this.selectedWorkflowId) {
      await this.loadWorkflow(this.selectedWorkflowId);
    }
  };

  private onAnnotationsChanged = (e: Event) => {
    const detail = (e as CustomEvent<{ annotations: Annotation[] }>).detail;
    if (!detail) return;
    this.annotations = detail.annotations ?? [];
    this.scheduleAnnotationSave();
  };

  private onAnnotationsUpdated = (annotations: Annotation[]) => {
    if (this.annotationSaveTimer) {
      window.clearTimeout(this.annotationSaveTimer);
      this.annotationSaveTimer = undefined;
    }
    this.annotations = annotations ?? [];
    this.editorApi?.setAnnotations?.(this.annotations);
  };

  private scheduleAnnotationSave() {
    if (!this.selectedWorkflowId) return;
    if (this.annotationSaveTimer) {
      window.clearTimeout(this.annotationSaveTimer);
    }
    this.annotationSaveTimer = window.setTimeout(() => {
      void this.saveAnnotations();
    }, 600);
  }

  private async saveAnnotations() {
    const workflowId = this.selectedWorkflowId;
    if (!workflowId) return;
    if (this.annotationSaveTimer) {
      window.clearTimeout(this.annotationSaveTimer);
      this.annotationSaveTimer = undefined;
    }
    try {
      await api.workflows.update(workflowId, {
        annotations: this.annotations,
      });
    } catch (error) {
      console.error('Failed to save annotations', error);
    }
  }

  private clearSelection() {
    this.updateTaskSelection([]);
    if (this.editorContainer) {
      this.editorContainer.dispatchEvent(
        new CustomEvent('selection-cleared', { bubbles: true, composed: true })
      );
    }
    this.editorApi?.clearVisitorCursors?.();
  }

  private async fetchWorkflowData(id: Id): Promise<WorkflowData> {
    const { workflow, tasks, connections, taskGroups } =
      await workflowService.fetchWorkflowData(id);
    this.workflow = workflow;
    return {
      workflow,
      tasks,
      connections,
      taskGroups,
      annotations: workflow.annotations,
    };
  }

  private applyTaskStateToEditor(taskId: number, state: TaskStateDetail) {
    void this.editorApi?.updateTaskState?.(taskId, {
      input: state.input ?? undefined,
      output: state.output ?? undefined,
      status: state.status,
      error:
        typeof state.error === 'string' || state.error === null
          ? state.error
          : undefined,
      disabled: state.disabled,
      interactionPending: state.interactionPending,
    });
  }

  private nextTaskStateRequestVersion(taskId: number): number {
    const version = (this.taskStateRequestVersions.get(taskId) ?? 0) + 1;
    this.taskStateRequestVersions.set(taskId, version);
    return version;
  }

  private async refreshTaskStateFromServer(workflowId: number, taskId: number) {
    if (this.taskStateRequests.has(taskId)) {
      this.taskStateRefreshQueuedIds.add(taskId);
      this.nextTaskStateRequestVersion(taskId);
      return;
    }
    const version = this.nextTaskStateRequestVersion(taskId);
    const request = workflowService.getTaskState(workflowId, taskId);
    this.taskStateRequests.set(taskId, request);
    try {
      const state = await request;
      if (
        this.selectedWorkflowId !== workflowId ||
        this.taskStateRequestVersions.get(taskId) !== version
      ) {
        return;
      }

      this.taskStateCache.set(taskId, state);
      this.applyTaskStateToEditor(taskId, state);
      this.applyTaskStateToTaskDetails(taskId, state, {
        stateLoading: false,
      });
    } finally {
      if (this.taskStateRequests.get(taskId) === request) {
        this.taskStateRequests.delete(taskId);
        this.taskStateLoadingIds.delete(taskId);
        const shouldRefreshAgain =
          this.taskStateRefreshQueuedIds.delete(taskId) &&
          this.selectedWorkflowId === workflowId;
        if (shouldRefreshAgain) {
          await this.refreshTaskStateFromServer(workflowId, taskId);
        }
      }
    }
  }

  private refreshEmittedTaskOutput(taskId: number) {
    const workflowId = this.selectedWorkflowId;
    if (typeof workflowId !== 'number') return;
    void this.refreshTaskStateFromServer(workflowId, taskId).catch((error) => {
      console.error('Failed to refresh emitted task output', error);
    });
  }

  private async fetchTaskState(
    taskId: number
  ): Promise<TaskStateDetail | null> {
    const workflowId = this.selectedWorkflowId;
    if (typeof workflowId !== 'number') return null;

    const cached = this.taskStateCache.get(taskId);
    if (cached) return cached;

    const pending = this.taskStateRequests.get(taskId);
    if (pending) return pending;

    const version = this.nextTaskStateRequestVersion(taskId);
    const request = workflowService
      .getTaskState(workflowId, taskId)
      .then((state) => {
        if (this.taskStateRequestVersions.get(taskId) !== version) {
          return this.taskStateCache.get(taskId) ?? state;
        }
        this.taskStateCache.set(taskId, state);
        this.applyTaskStateToEditor(taskId, state);
        return state;
      })
      .finally(() => {
        if (this.taskStateRequests.get(taskId) === request) {
          this.taskStateRequests.delete(taskId);
          this.taskStateLoadingIds.delete(taskId);
        }
      });

    this.taskStateRequests.set(taskId, request);
    this.taskStateLoadingIds.add(taskId);
    return request;
  }

  private async refreshWorkflowTaskStates(workflowId: number) {
    try {
      const tasks = await api.workflows.listTasks(workflowId);
      await Promise.all(
        tasks.map(async (task) => {
          const taskId = Number(task.id);
          if (!Number.isFinite(taskId)) return;

          await this.refreshTaskStateFromServer(workflowId, taskId);
        })
      );
    } catch (error) {
      console.error('Failed to refresh workflow task states', error);
    }
  }

  private async loadSelectedTaskState(taskId: number) {
    const selectedAtStart = this.selectedTaskId;
    let state: TaskStateDetail | null = null;
    try {
      state = await this.fetchTaskState(taskId);
    } catch (error) {
      console.error('Failed to load task state', error);
      this.applyTaskStateToTaskDetails(taskId, {}, { stateLoading: false });
      return;
    }
    if (!state || this.selectedTaskId !== selectedAtStart) return;

    this.applyTaskStateToTaskDetails(taskId, state, { stateLoading: false });
  }

  private async loadTaskConfig(taskId: number) {
    const workflowId = this.selectedWorkflowId;
    if (typeof workflowId !== 'number') return;

    let task: Task | undefined;
    try {
      const tasks = await api.workflows.listTasks(workflowId);
      task = tasks.find((candidate) => candidate.id === taskId);
    } catch (error) {
      console.error('Failed to load task config', error);
      return;
    }

    if (!task || this.selectedWorkflowId !== workflowId) {
      return;
    }

    const nextSelectedTaskConfig = syncRemoteTaskConfigUpdate({
      selectedTaskId: this.selectedTaskId,
      changedTaskId: taskId,
      config: task.config ?? {},
      updateTaskConfig: this.editorApi?.updateTaskConfig,
    });

    if (nextSelectedTaskConfig !== undefined) {
      this.selectedTaskConfig = nextSelectedTaskConfig;
    }
  }

  private createTaskDetailsViewModel(
    taskId: number,
    detail?: { label?: string; status?: string; error?: string | null }
  ): TaskDetailsBenchViewModel {
    const definition = this.editorApi?.getTaskDefinition?.(taskId);
    const rawName = detail?.label ?? definition?.name ?? `Task ${taskId}`;
    const name =
      typeof rawName === 'string' && rawName.trim().length
        ? rawName.trim()
        : `Task ${taskId}`;
    const label = this.editorApi?.getTaskLabel?.(taskId) ?? null;
    const cachedState = this.taskStateCache.get(taskId);
    const cachedError =
      typeof cachedState?.error === 'string' || cachedState?.error === null
        ? cachedState.error
        : undefined;

    return {
      id: taskId,
      name,
      label,
      status: detail?.status ?? cachedState?.status,
      error: detail?.error ?? cachedError,
      input: this.normalizeRecord(cachedState?.input),
      output: this.normalizeRecord(cachedState?.output),
      loopIteration: cachedState?.loopIteration ?? null,
      stateLoading: !cachedState || this.taskStateLoadingIds.has(taskId),
    };
  }

  private applyTaskStateToTaskDetails(
    taskId: number,
    state: Partial<
      Pick<
        TaskStateDetail,
        'input' | 'output' | 'status' | 'error' | 'loopIteration'
      >
    >,
    options?: { stateLoading?: boolean }
  ) {
    if (this.taskDetails?.id !== taskId) {
      return;
    }
    const hasInput = Object.hasOwn(state, 'input');
    const hasOutput = Object.hasOwn(state, 'output');
    const next: TaskDetailsBenchViewModel = {
      ...this.taskDetails,
      input: hasInput
        ? this.normalizeRecord(state.input)
        : this.taskDetails.input,
      output: hasOutput
        ? this.normalizeRecord(state.output)
        : this.taskDetails.output,
      status: state.status ?? this.taskDetails.status,
      error:
        typeof state.error === 'string' || state.error === null
          ? state.error
          : this.taskDetails.error,
      loopIteration:
        state.loopIteration !== undefined
          ? state.loopIteration
          : this.taskDetails.loopIteration,
      stateLoading:
        options?.stateLoading ??
        (hasInput && hasOutput ? false : this.taskDetails.stateLoading),
    };
    this.taskDetails = next;
  }

  private connectWorkflowSocket(id: Id) {
    const editorApi = this.editorApi;
    if (!editorApi) {
      return;
    }

    let markSocketReady = () => {};
    this.workflowSocketReady = new Promise<void>((resolve) => {
      markSocketReady = resolve;
    });
    websocketService.connectToWorkflow(id, {
      onMessage: (data) => {
        if (this.handleWorkflowAuditSocketEvent(id, data)) {
          return;
        }
        handleWorkflowEvent(
          {
            editorApi,
            visitors: this.visitors,
            setVisitors: (v) => {
              this.visitors = v;
              this.updateCursorThrottle();
            },
            onPendingInteractionChanged: () => {
              if (typeof id === 'number') {
                void this.refreshWorkflowPendingInteraction(id);
              }
            },
            onTaskUpdated: (task: Task) => {
              const taskId = Number(task?.id);
              if (!Number.isFinite(taskId)) return;
              const label = task?.label ?? null;
              if (this.selectedTaskId === taskId) {
                this.selectedTaskLabel = label;
                if (task.approvalGuards) {
                  this.selectedTaskApprovalGuards = {
                    ...task.approvalGuards,
                  };
                }
              }
              if (this.taskDetails?.id === taskId) {
                this.taskDetails = { ...this.taskDetails, label };
              }
            },
            onTaskConfigChanged: (taskId) => {
              void this.loadTaskConfig(taskId);
            },
            onTaskOutputEmitted: (taskId) =>
              this.refreshEmittedTaskOutput(taskId),
            onTaskStateChanged: (taskId, state) =>
              this.onTaskStateUpdate(taskId, state),
            onTaskGroupUpsert: (group) => this.upsertTaskGroupFromEvent(group),
            onTaskGroupRemoved: (groupId) =>
              this.removeTaskGroupFromEvent(groupId),
            onAnnotationsUpdated: (annotations) =>
              this.onAnnotationsUpdated(annotations),
            onWorkflowVersionChanged: (event) =>
              this.onWorkflowVersionChanged(event),
          },
          data as Parameters<typeof handleWorkflowEvent>[1]
        );
      },
      onOpen: markSocketReady,
      onClose: (_event) => void 0,
      onError: (error) =>
        console.error('🚨 WebSocket error for workflow', id, error),
    });
  }

  private handleWorkflowAuditSocketEvent(id: Id, data: unknown): boolean {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const payload = data as {
      type?: unknown;
      status?: unknown;
    };
    if (payload.type !== 'workflowAuditUpdated') {
      return false;
    }

    const status = String(payload.status ?? '').toLowerCase();
    if (
      typeof id === 'number' &&
      this.selectedWorkflowId === id &&
      (status === 'success' || status === 'failed')
    ) {
      void this.refreshWorkflowTaskStates(id);
    }

    return true;
  }

  private waitForWorkflowSocketReady(timeoutMs = 1_500) {
    return Promise.race([
      this.workflowSocketReady,
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, timeoutMs);
      }),
    ]);
  }

  private async registerVisitor(id: Id) {
    try {
      const me = visitorService.get();
      await workflowService.createVisitor(id, me);
      this.visitors = [...this.visitors.filter((v) => v.id !== me.id), me];
      this.updateCursorThrottle();
    } catch (err) {
      console.error('Failed to register visitor', err);
    }
  }

  private async loadWorkflow(id: Id) {
    this.loading = true;
    this.error = undefined;
    this.visitors = [];
    this.updateCursorThrottle();
    this.clearSelection();
    if (this.annotationSaveTimer) {
      window.clearTimeout(this.annotationSaveTimer);
      this.annotationSaveTimer = undefined;
    }
    this.userInputRequests = new Map();
    this.userInputActionTaskId = undefined;
    this.userInputActionType = undefined;
    this.taskDetails = undefined;
    this.versionEvents = [];
    this.versionEventsNextCursor = null;
    this.versionEventsHasMore = false;
    this.versionsLoading = false;
    this.versionsLoadingMore = false;
    this.selectedGroupId = undefined;
    this.taskStateCache.clear();
    this.taskStateRequests.clear();
    this.taskStateRequestVersions.clear();
    this.taskStateRefreshQueuedIds.clear();
    this.taskStateLoadingIds.clear();
    try {
      const data = await this.fetchWorkflowData(id);
      this.annotations = data.annotations ?? [];
      const editorData = {
        ...data.workflow,
        tasks: data.tasks,
        connections: data.connections,
        taskGroups: data.taskGroups,
        annotations: data.annotations,
      };
      await this.editorApi?.loadWorkflow?.(editorData);
      this.editorApi?.setWorkflowId?.(id);
      if (this.editorApi?.setTaskGroups) {
        await this.editorApi.setTaskGroups(data.taskGroups ?? []);
      }
      this.taskGroups = data.taskGroups ?? [];
      const definitions = await this.resolveTaskDefinitions();
      const definitionMap = new Map(definitions.map((d) => [d.name, d]));
      this.userInputRequests = await this.buildInitialUserInputRequests(
        id,
        data.tasks ?? [],
        definitionMap
      );
      const pendingRequest = toWorkflowUserInputRequest(
        data.workflow.pendingInteraction,
        definitionMap
      );
      if (pendingRequest) {
        this.userInputRequests = new Map(this.userInputRequests).set(
          pendingRequest.taskId,
          pendingRequest
        );
      }
      void this.loadVersionEvents(id);
      this.connectWorkflowSocket(id);
      await this.registerVisitor(id);
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.loading = false;
    }
  }

  private async loadLatestWorkflow() {
    try {
      const workflows = await workflowService.list();
      if (workflows.length) {
        const last = workflows[workflows.length - 1];
        this.selectedWorkflowId = last.id;
        await this.ensureEditorReady();
        await this.loadWorkflow(last.id);
      } else {
        const created = await workflowService.create();
        this.selectedWorkflowId = created.id;
        await this.ensureEditorReady();
        await this.loadWorkflow(created.id);
        this.workflowsList?.load?.();
      }
    } catch (e) {
      console.error(e);
    }
  }

  private getStoredWorkflowId(): Id | undefined {
    try {
      const raw = localStorage.getItem('kv.lastWorkflowId');
      if (!raw) return undefined;
      const id = Number(raw);
      return Number.isFinite(id) ? id : undefined;
    } catch (_) {
      return undefined;
    }
  }

  private async loadWorkflowById(id: Id): Promise<boolean> {
    await this.loadWorkflow(id);
    if (this.error) {
      this.error = undefined;
      return false;
    }
    return true;
  }

  private async loadInitialWorkflow() {
    const fromUrl = this.getWorkflowIdFromUrl();
    const fromStorage = this.getStoredWorkflowId();
    const candidate = fromUrl ?? fromStorage;
    if (candidate != null) {
      this.selectedWorkflowId = candidate;
      const success = await this.loadWorkflowById(candidate);
      if (success) {
        return;
      }
    }
    await this.loadLatestWorkflow();
  }

  private async loadTemplates() {
    this.templatesLoading = true;
    this.templatesError = null;
    try {
      const workflows = await workflowService.list();
      this.templates = workflows.filter((item) => item.isTemplate);
    } catch (error) {
      this.templatesError =
        error instanceof Error ? error.message : 'Failed to load templates';
      this.templates = [];
    } finally {
      this.templatesLoading = false;
    }
  }

  private async resolveTaskDefinitions(): Promise<TaskDefinition[]> {
    const taskData = this.taskDataTask.value as
      | { definitions?: TaskDefinition[] }
      | undefined;
    if (taskData?.definitions?.length) {
      return taskData.definitions;
    }
    try {
      const data = await taskService.fetchTaskData();
      return data.definitions ?? [];
    } catch {
      return [];
    }
  }

  private normalizeTemplateConfig(
    rawConfig: unknown,
    allowedKeys?: string[]
  ): Record<string, unknown> {
    if (!rawConfig || typeof rawConfig !== 'object') {
      return {};
    }
    const rawRecord = rawConfig as Record<string, unknown>;
    const source =
      'data' in rawRecord &&
      rawRecord.data &&
      typeof rawRecord.data === 'object'
        ? (rawRecord.data as Record<string, unknown>)
        : rawRecord;

    if (Array.isArray(allowedKeys)) {
      if (allowedKeys.length === 0) {
        return {};
      }
      const filtered: Record<string, unknown> = {};
      for (const key of allowedKeys) {
        if (Object.hasOwn(source, key)) {
          filtered[key] = source[key];
        }
      }
      return filtered;
    }
    return {};
  }

  private onTemplateBlank = () => {
    void this.createBlankWorkflowFromTemplateChooser();
  };

  private async createBlankWorkflowFromTemplateChooser() {
    if (this.templateApplying) {
      return;
    }
    this.templateApplying = true;
    this.templatesError = null;
    try {
      const result = await createBlankWorkflow({
        workflows: this.overviewWorkflows,
        createWorkflow: () => workflowService.create(),
      });
      this.overviewWorkflows = result.workflows;
      this.showTemplateChooser = false;
      await this.updateComplete;
      await this.openWorkflowInBuilder(result.workflow.id);
      this.workflowsList?.load?.();
    } catch (error) {
      this.templatesError =
        error instanceof Error ? error.message : 'Failed to create workflow';
    } finally {
      this.templateApplying = false;
    }
  }

  private onTemplateApply = (event: Event) => {
    const detail = (event as CustomEvent<{ template?: WorkflowSummary }>)
      .detail;
    if (!detail?.template) return;
    void this.applyTemplate(detail.template);
  };

  private onTemplateImport = (event: Event) => {
    const detail = (event as CustomEvent<{ file?: File }>).detail;
    if (!detail?.file) return;
    void this.importWorkflowFromTemplateChooser(detail.file);
  };

  private async importWorkflowFromTemplateChooser(file: File) {
    if (this.templateApplying) {
      return;
    }
    this.templateApplying = true;
    this.templatesError = null;
    try {
      const result = await importWorkflowFile({
        file,
        workflows: this.overviewWorkflows,
        importWorkflow: (selectedFile) => workflowService.import(selectedFile),
      });
      this.overviewWorkflows = result.workflows;
      this.showTemplateChooser = false;
      await this.updateComplete;
      await this.openWorkflowInBuilder(result.workflow.id);
      this.workflowsList?.load?.();
    } catch (error) {
      this.templatesError =
        error instanceof Error ? error.message : 'Failed to import workflow';
    } finally {
      this.templateApplying = false;
    }
  }

  private async applyTemplate(template: WorkflowSummary) {
    if (this.templateApplying) {
      return;
    }
    this.templateApplying = true;
    this.templatesError = null;
    let incompleteWorkflowId: Id | undefined;
    try {
      const templateData = await workflowService.fetchWorkflowData(template.id);
      const definitions = await this.resolveTaskDefinitions();
      const definitionMap = new Map(
        definitions.map((definition) => [definition.name, definition])
      );
      const created = await workflowService.create();
      const workflowId = created.id;
      incompleteWorkflowId = workflowId;
      const taskIdMap = new Map<Id, Id>();

      for (const task of templateData.tasks ?? []) {
        const created = await api.workflows.createTask(workflowId, {
          type: task.type,
          visual: task.visual,
        });
        taskIdMap.set(task.id, created.id);
        const definition = definitionMap.get(task.type);
        const allowedKeys = definition?.configDescription
          ? Object.keys(definition.configDescription)
          : undefined;
        const config = this.normalizeTemplateConfig(task.config, allowedKeys);
        if (Object.keys(config).length > 0) {
          await api.workflows.updateTask(workflowId, created.id, { config });
        }
      }

      for (const connection of templateData.connections ?? []) {
        const fromId = taskIdMap.get(connection.fromId);
        const toId = taskIdMap.get(connection.toId);
        if (!fromId || !toId) {
          continue;
        }
        await api.workflows.createConnection(workflowId, {
          fromId,
          toId,
          fromField: connection.fromField,
          toField: connection.toField,
        });
      }

      const groupIdMap = new Map<Id, Id>();
      for (const group of templateData.taskGroups ?? []) {
        const taskIds =
          group.taskIds
            ?.map((taskId) => taskIdMap.get(taskId))
            .filter((taskId): taskId is Id => typeof taskId === 'number') ?? [];
        const created = await api.workflows.createTaskGroup(workflowId, {
          title: group.title,
          taskIds,
        });
        groupIdMap.set(group.id, created.id);
      }

      for (const group of templateData.taskGroups ?? []) {
        if (!group.childGroupIds?.length) {
          continue;
        }
        const targetGroupId = groupIdMap.get(group.id);
        if (!targetGroupId) {
          continue;
        }
        const childGroupIds = group.childGroupIds
          .map((childId) => groupIdMap.get(childId))
          .filter((childId): childId is Id => typeof childId === 'number');
        if (childGroupIds.length === 0) {
          continue;
        }
        await api.workflows.updateTaskGroup(workflowId, targetGroupId, {
          childGroupIds,
        });
      }

      if (template.name || template.description) {
        await api.workflows.update(workflowId, {
          name: template.name ?? null,
          description: template.description ?? null,
          isTemplate: false,
        });
      }

      incompleteWorkflowId = undefined;
      this.overviewWorkflows = [...this.overviewWorkflows, created];
      this.showTemplateChooser = false;
      await this.updateComplete;
      await this.openWorkflowInBuilder(workflowId);
      this.workflowsList?.load?.();
    } catch (error) {
      if (incompleteWorkflowId != null) {
        try {
          await workflowService.remove(incompleteWorkflowId);
        } catch (cleanupError) {
          console.error(
            'Failed to remove incomplete template workflow',
            cleanupError
          );
        }
      }
      this.templatesError =
        error instanceof Error ? error.message : 'Failed to apply template';
    } finally {
      this.templateApplying = false;
    }
  }

  private async openWorkflowInBuilder(id: Id) {
    this.disconnectOverviewSockets();
    this.activeView = 'builder';
    this.selectedWorkflowId = id;
    await this.updateComplete;
    await this.ensureEditorReady();
    await this.loadWorkflow(id);
  }

  private showOverview = () => {
    this.activeView = 'overview';
    this.showTemplateChooser = false;
    this.teardownEditor();
    void this.loadOverviewWorkflows();
  };

  private formatWorkflowName(workflow: WorkflowSummary) {
    if (workflow.name && workflow.name.trim().length > 0) {
      return workflow.name.trim();
    }
    return `Automation ${workflow.id}`;
  }

  private setBusyId(
    current: Set<number>,
    id: number,
    busy: boolean
  ): Set<number> {
    const next = new Set(current);
    if (busy) {
      next.add(id);
    } else {
      next.delete(id);
    }
    return next;
  }

  private runWorkflowFromOverview = async (id: Id) => {
    this.overviewRunIds = this.setBusyId(this.overviewRunIds, Number(id), true);
    try {
      await workflowService.run(id);
    } catch (e) {
      this.overviewError = e instanceof Error ? e.message : String(e);
    } finally {
      this.overviewRunIds = this.setBusyId(
        this.overviewRunIds,
        Number(id),
        false
      );
    }
  };

  private createWorkflowFromOverview = () => {
    this.overviewError = null;
    Object.assign(
      this,
      openWorkflowStartChooser({
        teardownEditor: () => this.teardownEditor(),
        disconnectOverviewSockets: () => this.disconnectOverviewSockets(),
        loadTemplates: () => this.loadTemplates(),
      })
    );
  };

  private deleteWorkflowFromOverview = async (id: Id) => {
    this.overviewDeleteIds = this.setBusyId(
      this.overviewDeleteIds,
      Number(id),
      true
    );
    try {
      await workflowService.remove(id);
      this.overviewWorkflows = this.overviewWorkflows.filter(
        (workflow) => workflow.id !== id
      );
      this.syncOverviewSockets();
    } catch (e) {
      this.overviewError = e instanceof Error ? e.message : String(e);
    } finally {
      this.overviewDeleteIds = this.setBusyId(
        this.overviewDeleteIds,
        Number(id),
        false
      );
    }
  };

  private openAuditForWorkflow = (workflow: WorkflowSummary) => {
    const detail = {
      workflowId: workflow.id,
      workflowName: this.formatWorkflowName(workflow),
    };
    try {
      localStorage.setItem('kv.audit.filter', JSON.stringify(detail));
    } catch (_) {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent('audit-filter-change', { detail }));
    this.dispatchEvent(
      new CustomEvent('switch-module', {
        detail: { id: 'audit' },
        bubbles: true,
        composed: true,
      })
    );
  };

  private handleOverviewEvent(workflowId: number, data: unknown) {
    if (!data || typeof data !== 'object') return;
    const payload = data as {
      type?: string;
      workflow?: WorkflowSummary;
      workflowId?: number;
      pendingInteractionChanged?: boolean;
      auditId?: number;
      trigger?: string;
      status?: string;
      startedAt?: string | null;
      finishedAt?: string | null;
      durationMs?: number | null;
      cronLastExecutedAt?: string | null;
      cronNextExecutionAt?: string | null;
      taskId?: number;
    };

    if (payload.type === 'workflowCreated') {
      const created = payload.workflow;
      if (!created || typeof created.id !== 'number') return;
      if (this.overviewWorkflows.some((workflow) => workflow.id === created.id))
        return;
      this.overviewWorkflows = [...this.overviewWorkflows, created];
      this.syncOverviewSockets();
      return;
    }

    if (payload.type === 'workflowDeleted') {
      const deletedId = payload.workflowId;
      if (typeof deletedId !== 'number') return;
      if (!this.overviewWorkflows.some((workflow) => workflow.id === deletedId))
        return;
      this.overviewWorkflows = this.overviewWorkflows.filter(
        (workflow) => workflow.id !== deletedId
      );
      this.overviewRunIds = this.setBusyId(
        this.overviewRunIds,
        deletedId,
        false
      );
      this.overviewDeleteIds = this.setBusyId(
        this.overviewDeleteIds,
        deletedId,
        false
      );
      this.setOverviewUserInputRequest(deletedId, undefined);
      this.syncOverviewSockets();
      return;
    }

    if (payload.type === 'workflowUpdated') {
      this.overviewWorkflows = this.overviewWorkflows.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              cronLastExecutedAt:
                payload.cronLastExecutedAt ?? workflow.cronLastExecutedAt,
              cronNextExecutionAt:
                payload.cronNextExecutionAt ?? workflow.cronNextExecutionAt,
            }
          : workflow
      );
      return;
    }

    if (
      payload.type === 'taskStateChanged' ||
      payload.type === 'taskConfigChanged' ||
      payload.type === 'taskRemoved'
    ) {
      if (payload.pendingInteractionChanged) {
        void this.refreshOverviewPendingInteraction(workflowId);
      }
      return;
    }
    if (payload.type === 'workflowAuditCreated') {
      if (typeof payload.auditId !== 'number') return;
      const workflow = this.overviewWorkflows.find(
        (item) => item.id === workflowId
      );
      const workflowName = workflow
        ? this.formatWorkflowName(workflow)
        : `Automation ${workflowId}`;
      const audit: WorkflowAuditSummary = {
        id: payload.auditId,
        workflowId,
        workflowName,
        trigger:
          (payload.trigger as WorkflowAuditSummary['trigger']) ?? 'manual',
        status: (payload.status as WorkflowAuditSummary['status']) ?? 'running',
        startedAt:
          typeof payload.startedAt === 'string' ? payload.startedAt : null,
        finishedAt: null,
        durationMs: null,
      };
      const existing = this.overviewLatestAudits.get(workflowId);
      const existingTime = existing?.startedAt
        ? new Date(existing.startedAt).getTime()
        : 0;
      const auditTime = audit.startedAt
        ? new Date(audit.startedAt).getTime()
        : 0;
      if (!existing || auditTime >= existingTime) {
        this.setLatestOverviewAudit(workflowId, audit);
        this.overviewWorkflows = this.overviewWorkflows.map((workflow) =>
          workflow.id === workflowId
            ? {
                ...workflow,
                lastAuditId: audit.id,
                lastExecutedAt: audit.startedAt,
                lastAuditStatus: audit.status,
                failedTaskErrors: [],
                failedTaskErrorCount: 0,
              }
            : workflow
        );
      }
      return;
    }

    if (payload.type === 'workflowAuditUpdated') {
      if (typeof payload.auditId !== 'number') return;
      const current = this.overviewLatestAudits.get(workflowId);
      // current.id === workflowId means the entry was seeded by
      // syncOverviewAuditSummaries as a placeholder, so let the next audit
      // update replace it. Otherwise only update the tracked audit.
      if (
        !current ||
        (current.id !== workflowId && current.id !== payload.auditId)
      ) {
        return;
      }
      const next: WorkflowAuditSummary = {
        ...current,
        id: payload.auditId,
        status:
          (payload.status as WorkflowAuditSummary['status']) ?? current.status,
        finishedAt:
          typeof payload.finishedAt === 'string'
            ? payload.finishedAt
            : current.finishedAt,
        durationMs:
          typeof payload.durationMs === 'number'
            ? payload.durationMs
            : current.durationMs,
      };
      this.setLatestOverviewAudit(workflowId, next);
      this.overviewWorkflows = this.overviewWorkflows.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              lastAuditId: next.id,
              lastAuditStatus: next.status,
              failedTaskErrors:
                next.status === 'failed' ? workflow.failedTaskErrors : [],
              failedTaskErrorCount:
                next.status === 'failed' ? workflow.failedTaskErrorCount : 0,
            }
          : workflow
      );
      if (next.status === 'failed') {
        void this.loadOverviewFailedTaskErrors(workflowId, next.id);
      }
    }
  }

  private onOverviewRun = (event: Event) => {
    const detail = (event as CustomEvent<{ id?: Id }>).detail;
    if (!detail?.id) return;
    void this.runWorkflowFromOverview(detail.id);
  };

  private onOverviewOpen = (event: Event) => {
    const detail = (event as CustomEvent<{ id?: Id }>).detail;
    if (!detail?.id) return;
    void this.openWorkflowInBuilder(detail.id);
  };

  private onOverviewAudit = (event: Event) => {
    const detail = (event as CustomEvent<{ workflow?: WorkflowSummary }>)
      .detail;
    if (!detail?.workflow) return;
    this.openAuditForWorkflow(detail.workflow);
  };

  private onOverviewDelete = (event: Event) => {
    const detail = (event as CustomEvent<{ id?: Id }>).detail;
    if (!detail?.id) return;
    void this.deleteWorkflowFromOverview(detail.id);
  };

  private onOverviewInteractionAction = (event: Event) => {
    const detail = (
      event as CustomEvent<{
        workflowId?: number;
        taskId?: number;
        response?: TaskInteractionResponse;
        file?: File;
      }>
    ).detail;
    if (
      typeof detail?.workflowId !== 'number' ||
      typeof detail?.taskId !== 'number' ||
      !detail.response?.action
    ) {
      return;
    }
    void this.sendUserInputAction(
      detail.workflowId,
      detail.taskId,
      detail.response,
      detail.file
    );
  };

  private async withErrorHandling<T>(
    fn: () => Promise<T>
  ): Promise<T | undefined> {
    try {
      return await fn();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  private runWorkflow = async () => {
    const workflowId = this.selectedWorkflowId;
    if (typeof workflowId !== 'number') return;
    await this.withErrorHandling(async () => {
      await workflowService.reset(workflowId);
      await this.loadWorkflow(workflowId);
      await this.waitForWorkflowSocketReady();
      await workflowService.run(workflowId);
    });
  };

  private resetWorkflow = async () => {
    const workflowId = this.selectedWorkflowId;
    if (typeof workflowId !== 'number') return;
    await this.withErrorHandling(async () => {
      await workflowService.reset(workflowId);
      await this.loadWorkflow(workflowId);
    });
  };

  private syncUserInputState(
    taskId: number,
    state: {
      input?: Record<string, unknown>;
      interactionPending?: boolean;
      pendingInteraction?: PendingInteraction | null;
    }
  ) {
    const interactionPending = state.interactionPending;
    const next = new Map(this.userInputRequests);
    const existing = next.get(taskId);
    const definition = this.editorApi?.getTaskDefinition?.(taskId);
    const summary =
      state.pendingInteraction?.taskId === taskId
        ? state.pendingInteraction
        : undefined;
    const interaction = summary?.interaction ?? definition?.interaction;

    if (!interaction) {
      if (interactionPending === false && next.delete(taskId)) {
        this.userInputRequests = next;
      }
      return;
    }

    const config =
      this.editorApi?.getTaskConfig?.(taskId) ?? existing?.config ?? {};

    if (interactionPending === undefined) {
      return;
    }

    if (interactionPending) {
      const inputData =
        (state.input as { data?: Record<string, unknown> } | undefined)?.data ??
        state.input ??
        existing?.input ??
        {};
      next.set(taskId, {
        taskId,
        taskType: definition?.name ?? summary?.taskType ?? '',
        input: inputData,
        config,
        interaction,
      });
      this.userInputRequests = next;
      return;
    }

    if (interactionPending === false) {
      if (next.delete(taskId)) this.userInputRequests = next;
      if (this.userInputActionTaskId === taskId) {
        this.userInputActionTaskId = undefined;
        this.userInputActionType = undefined;
      }
    }
  }

  private async sendUserInputAction(
    workflowId: number,
    taskId: number,
    response: TaskInteractionResponse,
    file?: File
  ) {
    this.userInputActionTaskId = taskId;
    this.userInputActionType =
      typeof response.action === 'string' ? response.action : undefined;
    try {
      await this.withErrorHandling(() => {
        if (file) {
          return workflowService.uploadTaskInteractionFile(workflowId, taskId, {
            file,
            response,
          });
        }

        return workflowService.respondToTaskInteraction(
          workflowId,
          taskId,
          response
        );
      });
    } finally {
      if (this.userInputActionTaskId === taskId) {
        this.userInputActionTaskId = undefined;
        this.userInputActionType = undefined;
      }
    }
  }

  private onUserInputAction = (e: Event) => {
    const detail = (
      e as CustomEvent<{
        taskId: number;
        response: TaskInteractionResponse;
        file?: File;
      }>
    ).detail;
    const workflowId = this.selectedWorkflowId;
    if (typeof workflowId !== 'number' || !detail) return;
    void this.sendUserInputAction(
      workflowId,
      detail.taskId,
      detail.response,
      detail.file
    );
  };

  private onAddTask = async (e: Event) => {
    const detail = (e as CustomEvent<{ type: string }>).detail;
    const workflowId = Number(this.selectedWorkflowId);
    if (!detail?.type || !Number.isFinite(workflowId)) return;
    await this.withErrorHandling(async () => {
      await workflowService.createTask(workflowId, {
        type: detail.type,
      });
      await this.loadWorkflow(workflowId);
    });
    this.showTemplateChooser = false;
  };

  private onDragOver = (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  private onDrop = async (e: DragEvent) => {
    e.preventDefault();
    const workflowId = Number(this.selectedWorkflowId);
    if (!Number.isFinite(workflowId)) return;
    try {
      const text = e.dataTransfer?.getData('text/plain');
      const { type, config } = JSON.parse(text ?? '');
      if (!type) return;
      await this.ensureEditorReady();
      const pointer = this.editorApi?.getPointer?.(e);
      const visual =
        pointer && Number.isFinite(pointer.x) && Number.isFinite(pointer.y)
          ? pointer
          : { x: 160, y: 140 };
      const task = await api.workflows.createTask(workflowId, {
        type,
        visual,
      });
      if (config && Object.keys(config).length) {
        await api.workflows.updateTask(workflowId, task.id, {
          config,
        });
      }
      await this.loadWorkflow(workflowId);
      this.showTemplateChooser = false;
    } catch (err: any) {
      console.error(err, err.status, err.body, e);
    }
  };

  private onTaskStateUpdate = (
    taskId: number,
    state: {
      status?: string;
      disabled?: boolean;
      interactionPending?: boolean;
      loopIteration?: LoopIterationContext | null;
      pendingInteractionChanged?: boolean;
    }
  ) => {
    this.taskStateCache.delete(taskId);
    if (
      !state.pendingInteractionChanged &&
      state.interactionPending === false
    ) {
      this.syncUserInputState(taskId, { interactionPending: false });
    }
    this.applyTaskStateToTaskDetails(taskId, state, {
      stateLoading: this.taskDetails?.id === taskId ? true : undefined,
    });
    if (this.selectedTaskId === taskId) {
      void this.loadSelectedTaskState(taskId);
    }
  };

  private isFromEditable = (e: Event): boolean => {
    const path = e.composedPath() as EventTarget[];
    for (const t of path) {
      if (!(t instanceof HTMLElement)) continue;
      if (t.isContentEditable) return true;
      if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement)
        return true;
      if (
        t.closest?.(
          'input, textarea, [contenteditable=""], [contenteditable="true"], [role="textbox"]'
        )
      )
        return true;
    }
    let el: Element | null = document.activeElement;
    while (el && (el as any).shadowRoot?.activeElement) {
      el = (el as any).shadowRoot.activeElement;
    }
    return (
      !!el && el.matches('input, textarea, [contenteditable], [role="textbox"]')
    );
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (this.isFromEditable(e)) return;
    if (this.activeView === 'builder' && this.isTaskSearchHotkey(e)) {
      e.preventDefault();
      void this.focusTaskSearch();
      return;
    }
    if (
      e.key === 'Escape' &&
      this.taskDetails &&
      this.taskDetailsBenchExpanded
    ) {
      e.preventDefault();
      this.setTaskDetailsBenchExpanded(false);
      return;
    }
    const key = e.key.toLowerCase();
    if (e.ctrlKey || e.metaKey) {
      switch (key) {
        case 'v':
          e.preventDefault();
          this.editorApi?.pasteSelection?.();
          return;
        case 'c':
          if (window.getSelection()?.toString()) {
            return;
          }
          if (
            this.selectedTaskId != null ||
            this.editorApi?.hasSelectedAnnotation?.()
          ) {
            e.preventDefault();
            const hasAnnotation =
              this.editorApi?.hasSelectedAnnotation?.() ?? false;
            this.editorApi?.copySelection(
              hasAnnotation ? undefined : this.selectedTaskId
            );
          }
          return;
        case 'x':
          if (window.getSelection()?.toString()) {
            return;
          }
          if (
            this.selectedTaskId != null ||
            this.editorApi?.hasSelectedAnnotation?.()
          ) {
            e.preventDefault();
            const hasAnnotation =
              this.editorApi?.hasSelectedAnnotation?.() ?? false;
            this.editorApi?.cutSelection(
              hasAnnotation ? undefined : this.selectedTaskId
            );
          }
          return;
      }
    }
    if (this.handleGroupHotkey(e)) return;
    if (!e.ctrlKey && !e.metaKey && this.selectedTaskId != null) {
      if (key === 'delete' || key === 'backspace') {
        e.preventDefault();
        this.editorApi?.deleteTask(this.selectedTaskId);
        return;
      }
    }
    if (e.ctrlKey || e.metaKey) {
      if (e.shiftKey && key === 'g') {
        if (this.selectedGroupId != null) {
          e.preventDefault();
          this.ungroup(this.selectedGroupId);
        }
        return;
      }
    }
  };

  private grouping = false;

  private async groupSelectedTasks() {
    if (!this.selectedWorkflowId) return;
    if (this.grouping) return;
    const taskIds = this.selectedTaskIds.filter((id) => Number.isFinite(id));
    const childGroupIds = this.selectedGroupIds.filter((id) =>
      Number.isFinite(id as number)
    );
    const selectionCount = new Set([...taskIds, ...childGroupIds]).size;
    if (selectionCount < 2) return;
    this.error = undefined;
    const title = `Group ${this.taskGroups.length + 1}`;
    this.grouping = true;
    try {
      const group = await workflowService.createTaskGroup(
        this.selectedWorkflowId,
        {
          title,
          taskIds,
          childGroupIds,
        }
      );
      // Optimistically add the new group without reloading tasks/positions
      this.taskGroups = [...this.taskGroups, group];
      await this.editorApi?.setTaskGroups?.(this.taskGroups);
      this.selectedGroupId = group.id;
      this.selectedGroupIds = [group.id];
      this.editorApi?.setSelectedGroup?.([group.id]);
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
    } finally {
      this.grouping = false;
    }
  }

  private upsertTaskGroupFromEvent(group: TaskGroup) {
    if (!group?.id) return;
    const existingIdx = this.taskGroups.findIndex((g) => g.id === group.id);
    const next =
      existingIdx >= 0
        ? this.taskGroups.map((g, idx) =>
            idx === existingIdx ? { ...g, ...group } : g
          )
        : [...this.taskGroups, group];
    this.taskGroups = next;
    if (this.selectedGroupId === group.id) {
      this.groupEditTitle = group.title ?? this.groupEditTitle;
    }
    void this.editorApi?.setTaskGroups?.(next);
  }

  private removeTaskGroupFromEvent(groupId: number) {
    const next = this.taskGroups.filter((g) => g.id !== groupId);
    if (next.length === this.taskGroups.length) return;
    this.taskGroups = next;
    if (this.selectedGroupId === groupId) {
      this.updateGroupSelection([]);
      this.updateTaskSelection([]);
    }
    void this.editorApi?.setTaskGroups?.(next);
  }

  private async ungroup(groupId?: Id) {
    if (!this.selectedWorkflowId) return;
    const targetId = groupId ?? this.selectedGroupId;
    if (targetId == null) return;
    this.error = undefined;
    try {
      await workflowService.deleteTaskGroup(this.selectedWorkflowId, targetId);
      // Remove locally without reloading tasks/positions
      const remaining = this.taskGroups.filter((g) => g.id !== targetId);
      this.taskGroups = remaining;
      await this.editorApi?.setTaskGroups?.(remaining);
      if (this.selectedGroupId === targetId) {
        this.selectedGroupId = undefined;
        this.selectedGroupIds = [];
        this.groupEditTitle = '';
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
    }
  }

  private setActiveTab(name: string) {
    this.activeTab = name;
    if (this.tabsEl) this.tabsEl.selected = name;
  }

  private onTabChange = (e: Event) => {
    const selected = (e.target as any)?.selected ?? this.activeTab;
    this.activeTab = selected;
    if (this.activeTab !== 'inspector') {
      this.previousTab = this.activeTab;
    }
    if (this.activeTab === 'versions' && this.selectedWorkflowId) {
      void this.loadVersionEvents(this.selectedWorkflowId);
    }
  };

  private clearTaskSelectionDetails() {
    this.selectedTaskId = undefined;
    this.selectedTaskConfig = undefined;
    this.selectedTaskApprovalGuards = undefined;
    this.selectedTaskDefinition = undefined;
    this.selectedTaskLabel = null;
    this.taskDetails = undefined;
  }

  private clearConnectionSelectionDetails(options?: {
    preserveInspectorTab?: boolean;
  }) {
    this.selectedConnectionId = undefined;
    this.selectedConnection = undefined;
    if (
      !options?.preserveInspectorTab &&
      this.activeTab === 'inspector' &&
      !this.selectedTaskId &&
      !this.selectedGroupId
    ) {
      this.setActiveTab(this.previousTab);
    }
  }

  private updateTaskSelection(ids: number[]) {
    this.clearConnectionSelectionDetails({ preserveInspectorTab: true });
    const normalized = Array.from(
      new Set(ids.map((id) => Number(id)).filter((id) => Number.isFinite(id)))
    );
    this.selectedTaskIds = normalized;
    if (normalized.length === 1) {
      const id = normalized[0];
      this.selectedTaskId = id;
      const config = this.editorApi?.getTaskConfig?.(id) || {};
      const approvalGuards = this.editorApi?.getTaskApprovalGuards?.(id);
      const def = this.editorApi?.getTaskDefinition?.(id);
      this.selectedTaskConfig = { ...config };
      this.selectedTaskApprovalGuards = approvalGuards
        ? { ...approvalGuards }
        : { preExecution: false, postExecution: false };
      this.selectedTaskDefinition = def;
      this.selectedTaskLabel = this.editorApi?.getTaskLabel?.(id) ?? null;
      this.taskDetails = this.createTaskDetailsViewModel(id);
      void this.loadSelectedTaskState(id);
      if (this.activeTab !== 'inspector') {
        this.previousTab = this.activeTab;
        this.setActiveTab('inspector');
      }
      return;
    }
    this.clearTaskSelectionDetails();
    if (this.activeTab === 'inspector') {
      this.setActiveTab(this.previousTab);
    }
  }

  private updateGroupSelection(groupIds: Id[]) {
    this.clearConnectionSelectionDetails({ preserveInspectorTab: true });
    const normalized = Array.from(
      new Set(
        groupIds
          .map((g) => {
            const n = Number(g);
            return Number.isFinite(n) ? n : g;
          })
          .filter((g) => g !== undefined && g !== null)
      )
    ) as Id[];

    this.selectedGroupIds = normalized;
    const first = normalized[0];
    this.selectedGroupId = first;

    if (first != null) {
      const found = this.taskGroups.find((g) => g.id === first);
      this.groupEditTitle = found?.title ?? this.groupEditTitle;
      this.editorApi?.setSelectedGroup?.(normalized);
      if (this.activeTab !== 'inspector') {
        this.previousTab = this.activeTab;
        this.setActiveTab('inspector');
      }
      return;
    }

    this.selectedGroupId = undefined;
    this.groupEditTitle = '';
    this.editorApi?.setSelectedGroup?.([]);
  }

  private updateConnectionSelection(connection: Connection) {
    this.selectedTaskIds = [];
    this.clearTaskSelectionDetails();
    this.selectedGroupIds = [];
    this.selectedGroupId = undefined;
    this.groupEditTitle = '';
    this.editorApi?.setSelectedGroup?.([]);

    this.selectedConnectionId = connection.id;
    this.selectedConnection = { ...connection };
    if (this.activeTab !== 'inspector') {
      this.previousTab = this.activeTab;
      this.setActiveTab('inspector');
    }
  }

  private onTasksSelectionChanged = (e: Event) => {
    const detail = (e as CustomEvent<{ ids?: unknown[]; groupIds?: unknown[] }>)
      .detail;
    const ids = Array.isArray(detail?.ids) ? detail.ids : [];
    this.updateTaskSelection(ids as number[]);

    const groupIds = Array.isArray(detail?.groupIds) ? detail.groupIds : [];
    this.updateGroupSelection(groupIds as Id[]);
  };

  private onConnectionSelected = (e: Event) => {
    const detail = (e as CustomEvent<Connection>).detail;
    if (!detail?.id) {
      return;
    }
    this.updateConnectionSelection(detail);
  };

  private onConnectionSelectionCleared = () => {
    this.clearConnectionSelectionDetails();
  };

  private onTaskDoubleClicked = (e: Event) => {
    const detail = (
      e as CustomEvent<{
        id?: number;
        label?: string;
        status?: string;
        error?: string | null;
      }>
    ).detail;
    if (detail?.id == null) {
      return;
    }
    const taskId = Number(detail.id);
    if (!Number.isFinite(taskId)) {
      return;
    }

    if (this.selectedTaskId !== taskId) {
      this.updateTaskSelection([taskId]);
    }
    this.taskDetails = this.createTaskDetailsViewModel(taskId, detail);
    this.setTaskDetailsBenchExpanded(true);

    const cachedState = this.taskStateCache.get(taskId);
    if (!cachedState) {
      void this.fetchTaskState(taskId)
        .then((state) => {
          if (!state || this.taskDetails?.id !== taskId) return;
          this.applyTaskStateToTaskDetails(taskId, state, {
            stateLoading: false,
          });
        })
        .catch((error) => {
          if (this.taskDetails?.id !== taskId) return;
          this.taskDetails = {
            ...this.taskDetails,
            error: error instanceof Error ? error.message : String(error),
            stateLoading: false,
          };
        });
    }
  };

  private onSelectionCleared = () => {
    this.updateTaskSelection([]);
    this.updateGroupSelection([]);
    this.clearConnectionSelectionDetails();
  };

  private onTaskConfigUpdate = (
    e: CustomEvent<{ key: string; value: unknown; taskId?: Id }>
  ) => {
    const { key, value, taskId } = e.detail;
    this.selectedTaskConfig = syncLocalTaskConfigUpdate({
      selectedTaskId: this.selectedTaskId,
      changedTaskId: taskId,
      selectedTaskConfig: this.selectedTaskConfig,
      updateTaskConfig: this.editorApi?.updateTaskConfig,
      key,
      value,
    });
  };

  private onTaskLabelUpdate = (e: CustomEvent<{ value: string | null }>) => {
    this.selectedTaskLabel = e.detail.value;
    const taskDetails = this.taskDetails;
    if (!taskDetails || taskDetails.id !== this.selectedTaskId) {
      return;
    }
    this.taskDetails = {
      ...taskDetails,
      label: e.detail.value,
    };
  };

  private onTaskApprovalGuardsUpdate = (
    e: CustomEvent<{ value: TaskApprovalGuards }>
  ) => {
    const taskId = this.selectedTaskId;
    this.selectedTaskApprovalGuards = { ...e.detail.value };
    if (typeof taskId === 'number') {
      this.editorApi?.updateTaskApprovalGuards?.(taskId, e.detail.value);
    }
  };

  private onConnectionNameUpdate = (
    e: CustomEvent<{ id: number; value: string }>
  ) => {
    const { id, value } = e.detail;
    if (this.selectedConnectionId === id && this.selectedConnection) {
      this.selectedConnection = {
        ...this.selectedConnection,
        name: value,
      };
    }
    this.editorApi?.updateConnectionName?.(id, value);
  };

  private onTaskGroupConfigUpdate = (
    e: CustomEvent<{ key: string; value: unknown }>
  ) => {
    const { key, value } = e.detail;
    if (key !== 'title') return;
    const title = typeof value === 'string' ? value : String(value ?? '');
    if (this.selectedGroupId != null) {
      this.taskGroups = this.taskGroups.map((g) =>
        g.id === this.selectedGroupId ? { ...g, title } : g
      );
    }
  };

  private onTaskDetailsBenchExpandedChange = (
    e: CustomEvent<{ expanded: boolean }>
  ) => {
    this.setTaskDetailsBenchExpanded(!!e.detail.expanded);
  };

  private onTaskDetailsBenchHeightChange = (
    e: CustomEvent<{ height: number }>
  ) => {
    this.setTaskDetailsBenchHeight(Number(e.detail.height));
  };

  private normalizeRecord(
    data?: Record<string, unknown> | null
  ): Record<string, unknown> | undefined {
    if (!data) {
      return undefined;
    }
    const entries = Object.entries(data);
    if (entries.length === 0) {
      return undefined;
    }
    return Object.fromEntries(entries);
  }

  private renderTaskDetailsBench() {
    const taskDetails = this.taskDetails;
    if (!taskDetails) {
      return nothing;
    }

    return html`
      <task-details-bench
        .taskDetails=${taskDetails}
        .expanded=${this.taskDetailsBenchExpanded}
        .height=${this.taskDetailsBenchHeight}
        @bench-expanded-change=${this.onTaskDetailsBenchExpandedChange}
        @bench-height-change=${this.onTaskDetailsBenchHeightChange}
      ></task-details-bench>
    `;
  }

  private async loadVersionEvents(workflowId: number, append = false) {
    if (append) {
      if (!this.versionEventsHasMore || this.versionsLoadingMore) return;
      this.versionsLoadingMore = true;
    } else {
      this.versionsLoading = true;
    }
    try {
      const page = await api.workflows.listVersions(workflowId, {
        beforeId: append ? this.versionEventsNextCursor : null,
        limit: 50,
      });
      if (this.selectedWorkflowId !== workflowId) return;
      const events = Array.isArray(page.events) ? page.events : [];
      this.versionEvents = append
        ? [
            ...this.versionEvents,
            ...events.filter(
              (event) =>
                !this.versionEvents.some((existing) => existing.id === event.id)
            ),
          ]
        : events;
      this.versionEventsNextCursor = page.nextCursor;
      this.versionEventsHasMore = page.hasMore;
    } catch {
      if (this.selectedWorkflowId !== workflowId) return;
      if (!append) {
        this.versionEvents = [];
        this.versionEventsNextCursor = null;
        this.versionEventsHasMore = false;
      }
    } finally {
      if (this.selectedWorkflowId === workflowId) {
        if (append) {
          this.versionsLoadingMore = false;
        } else {
          this.versionsLoading = false;
        }
      }
    }
  }

  private onWorkflowVersionChanged(event: WorkflowVersionEvent) {
    if (event.workflowId !== this.selectedWorkflowId) return;
    this.versionEvents = [
      event,
      ...this.versionEvents.filter((item) => item.id !== event.id),
    ];
  }

  private requestRestoreVersion = (event: WorkflowVersionEvent) => {
    this.pendingRestoreVersion = event;
  };

  private cancelRestoreVersion = () => {
    this.pendingRestoreVersion = null;
  };

  private confirmRestoreVersion = () => {
    const event = this.pendingRestoreVersion;
    if (!event) return;
    this.pendingRestoreVersion = null;
    void this.restoreVersion(event);
  };

  private restoreVersion = async (event: WorkflowVersionEvent) => {
    const workflowId = this.selectedWorkflowId;
    if (workflowId == null) return;
    this.restoringVersionId = event.id;
    try {
      await api.workflows.restoreVersion(workflowId, event.id);
      await this.loadWorkflow(workflowId);
      await this.workflowsList?.load?.();
    } catch (error) {
      this.error =
        error instanceof Error
          ? error.message
          : msg('Failed to restore automation version');
    } finally {
      if (this.restoringVersionId === event.id) {
        this.restoringVersionId = null;
      }
    }
  };

  private renderVersionsPanel() {
    return html`
      <kv-version-history-panel
        .events=${this.versionEvents}
        .loading=${this.versionsLoading}
        .loadingMore=${this.versionsLoadingMore}
        .hasMore=${this.versionEventsHasMore}
        .restoringVersionId=${this.restoringVersionId}
        @restore-version=${(
          event: CustomEvent<{ event: WorkflowVersionEvent }>
        ) => this.requestRestoreVersion(event.detail.event)}
        @load-more-versions=${() => {
          if (this.selectedWorkflowId == null) return;
          void this.loadVersionEvents(this.selectedWorkflowId, true);
        }}
      ></kv-version-history-panel>
    `;
  }

  private renderRestoreVersionDialog() {
    if (!this.pendingRestoreVersion) {
      return nothing;
    }

    return html`
      <kv-confirmation-dialog
        heading=${msg('Restore automation version')}
        confirm-label=${msg('Restore')}
        @cancel=${this.cancelRestoreVersion}
        @confirm=${this.confirmRestoreVersion}
      >
        <p>
          ${msg(
            str`Restore "${this.workflow ? this.formatWorkflowName(this.workflow) : msg('this automation')}" to before ${formatDateTime(this.pendingRestoreVersion.createdAt)}? The current automation state will be replaced by that version.`
          )}
        </p>
      </kv-confirmation-dialog>
    `;
  }

  private renderSidebarTabPanel() {
    switch (this.activeTab) {
      case 'task-tree':
        return this.taskDataTask.render({
          pending: () =>
            html`<sp-progress-circle
              label=${msg('Loading content')}
              indeterminate
            ></sp-progress-circle>`,
          error: (e: unknown) =>
            html`<div>${e instanceof Error ? e.message : String(e)}</div>`,
          complete: (value: TaskData) => html`
            <sb-task-tree
              .categories=${value.categories}
              .definitions=${value.definitions}
              .filter=${''}
              @add-task=${this.onAddTask}
            ></sb-task-tree>
          `,
        });
      case 'workflow-agent':
        return html`
          <workflow-agent
            .workflowId=${this.selectedWorkflowId}
          ></workflow-agent>
        `;
      case 'inspector':
        return this.renderInspectorPanel();
      case 'settings':
        return html`
          <sb-settings
            .workflowId=${this.selectedWorkflowId}
            .workflowsList=${this.workflowsList}
          ></sb-settings>
        `;
      case 'versions':
        return this.renderVersionsPanel();
      default:
        return nothing;
    }
  }

  private renderInspectorPanel() {
    if (this.selectedConnectionId && this.selectedConnection) {
      return html`
        <sb-connection-inspector
          .workflowId=${this.selectedWorkflowId}
          .connectionId=${this.selectedConnectionId}
          .connectionName=${this.selectedConnection.name}
          @connection-name-changed=${this.onConnectionNameUpdate}
        ></sb-connection-inspector>
      `;
    }

    if (this.selectedTaskId) {
      return html`
        <sb-task-inspector
          .workflowId=${this.selectedWorkflowId}
          .taskId=${this.selectedTaskId}
          .label=${this.selectedTaskLabel}
          .config=${this.selectedTaskConfig}
          .approvalGuards=${this.selectedTaskApprovalGuards}
          .definition=${this.selectedTaskDefinition}
          @task-config-changed=${this.onTaskConfigUpdate}
          @task-approval-guards-changed=${this.onTaskApprovalGuardsUpdate}
          @task-label-changed=${this.onTaskLabelUpdate}
        ></sb-task-inspector>
      `;
    }

    if (this.selectedGroupId) {
      return html`
        <sb-task-group-inspector
          .workflowId=${this.selectedWorkflowId}
          .taskGroupId=${this.selectedGroupId}
          .taskGroup=${this.taskGroups.find(
            (g) => g.id === this.selectedGroupId
          )}
          @task-group-config-changed=${this.onTaskGroupConfigUpdate}
        ></sb-task-group-inspector>
      `;
    }

    return nothing;
  }

  private renderSidebar() {
    return html`
      <div class="sidebar">
        <sb-workflows
          .selectedWorkflowId=${this.selectedWorkflowId}
          @open-workflow=${this.openWorkflow}
          @show-overview=${this.showOverview}
          @workflow-modified=${this.onWorkflowModified}
          @create-workflow=${this.createWorkflowFromOverview}
          @run-workflow=${this.runWorkflow}
          @reset-workflow=${this.resetWorkflow}
        ></sb-workflows>
        <sp-tabs-overflow compact size="s">
          <sp-tabs
            compact
            dir="ltr"
            .selected=${this.activeTab}
            @change=${this.onTabChange}
            size="s"
          >
            <sp-tab value="task-tree">${msg('Tasks')}</sp-tab>
            <sp-tab value="workflow-agent">${msg('Agent')}</sp-tab>
            <sp-tab
              value="inspector"
              ?disabled=${
                !this.selectedTaskId &&
                !this.selectedGroupId &&
                !this.selectedConnectionId
              }
            >
              ${msg('Properties')}
            </sp-tab>
            <sp-tab value="settings">${msg('Settings')}</sp-tab>
            <sp-tab value="versions">${msg('Versions')}</sp-tab>
          </sp-tabs>
        </sp-tabs-overflow>
        <div class="sidebar-tab-panel">${this.renderSidebarTabPanel()}</div>
      </div>
    `;
  }

  private renderVisitors() {
    if (isSingleVisitor(this.visitors)) {
      return nothing;
    }
    return html`
      <div class="visitors">
        ${this.visitors.map(
          (v) =>
            html`<div class="visitor">
              <span class="avatar" style="background:${v.color}"></span>
              <span class="name">${v.name}</span>
            </div>`
        )}
      </div>
    `;
  }

  private renderUserInputOverlay() {
    if (this.userInputRequests.size === 0) {
      return nothing;
    }
    const [request] = this.userInputRequests.values();
    if (!request) {
      return nothing;
    }

    const isProcessing = this.userInputActionTaskId === request.taskId;

    return html`
      <kv-workflow-user-input-dialog
        .request=${request}
        .processing=${isProcessing}
        .processingAction=${this.userInputActionType}
        @interaction-action=${this.onUserInputAction}
      ></kv-workflow-user-input-dialog>
    `;
  }

  private renderEditorLoading() {
    if (!this.loading) {
      return nothing;
    }
    return html`
      <sp-underlay class="editor-underlay" open></sp-underlay>
      <sp-progress-circle
        class="editor-spinner"
        label=${msg('Loading automation')}
        indeterminate
      ></sp-progress-circle>
    `;
  }

  private renderMain() {
    return html`
      <div class="main" data-workflow-id="${this.selectedWorkflowId}">
        <div class="editor-wrap">
          <div class="canvas-wrap">
            ${this.renderVisitors()}
            ${this.renderEditorLoading()}
            <div
              class="rete"
              @dragover=${this.onDragOver}
              @drop=${this.onDrop}
              @tasks-selection-changed=${this.onTasksSelectionChanged}
              @selection-cleared=${this.onSelectionCleared}
              @connection-selected=${this.onConnectionSelected}
              @connection-selection-cleared=${this.onConnectionSelectionCleared}
              @annotations-changed=${this.onAnnotationsChanged}
            ></div>
            ${
              this.showTemplateChooser
                ? html`<kv-template-chooser
                    .templates=${this.templates}
                    .loading=${this.templatesLoading}
                    .error=${this.templatesError}
                    .applying=${this.templateApplying}
                    @start-blank=${this.onTemplateBlank}
                    @import-workflow=${this.onTemplateImport}
                    @apply-template=${this.onTemplateApply}
                    @back-to-list=${this.showOverview}
                  ></kv-template-chooser>`
                : nothing
            }
          </div>
          ${this.renderTaskDetailsBench()}
          ${this.renderUserInputOverlay()}
        </div>
      </div>
    `;
  }
  private resetWorkflowFromOverview = async (event: Event) => {
    const detail = (event as CustomEvent<{ id?: Id }>).detail;
    const workflowId = detail?.id;
    if (typeof workflowId !== 'number') return;
    try {
      await workflowService.reset(workflowId);
      await this.applyOverviewPendingInteraction(workflowId, null);
      const nextAudits = new Map(this.overviewLatestAudits);
      nextAudits.delete(workflowId);
      this.overviewLatestAudits = nextAudits;
      this.overviewWorkflows = this.overviewWorkflows.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              lastAuditId: null,
              lastAuditStatus: null,
              failedTaskErrors: [],
              failedTaskErrorCount: 0,
              pendingInteraction: null,
            }
          : workflow
      );
    } catch (e) {
      this.overviewError = e instanceof Error ? e.message : String(e);
    }
  };

  render() {
    return html`
      ${
        this.activeView === 'overview'
          ? html`<kv-workflow-overview
            .workflows=${this.overviewWorkflows}
            .loading=${this.overviewLoading}
            .errorMessage=${this.overviewError}
            .runIds=${this.overviewRunIds}
            .deleteIds=${this.overviewDeleteIds}
            .userInputRequests=${this.overviewUserInputRequests}
            .userInputActionTaskId=${this.userInputActionTaskId}
            .userInputActionType=${this.userInputActionType}
            @create-workflow=${this.createWorkflowFromOverview}
            @run-workflow=${this.onOverviewRun}
            @reset-workflow=${this.resetWorkflowFromOverview}
            @open-workflow=${this.onOverviewOpen}
            @audit-workflow=${this.onOverviewAudit}
            @delete-workflow=${this.onOverviewDelete}
            @overview-interaction-action=${this.onOverviewInteractionAction}
          ></kv-workflow-overview>`
          : this.showTemplateChooser
            ? this.renderMain()
            : html`<sp-split-view
                class="builder"
                resizable
                primary-size="440px"
                .primaryMin=${320}
                .primaryMax=${560}
                label=${msg('Resize automation sidebar')}
              >
                ${this.renderSidebar()}
                ${this.renderMain()}
              </sp-split-view>`
      }
      ${this.renderRestoreVersionDialog()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-module-workflow': KvModuleWorkflow;
  }
}
